import React from "react";
import { Truck, Loader } from "lucide-react";
import { AssignedCart, Group } from "../../../../redux/deliveryRouteOptimizer";

interface CartAssignmentSectionProps {
  groups: Group[];
  vehicles: AssignedCart[];
  assignVehicleToGroup: (groupId: string, vehicleId: string) => void;
  getOptimizedRouteOrder: (group: Group) => string[];
  getVehicleDisplayName: (vehicle: AssignedCart) => string;
  isGroupAssigning: (groupId: string) => boolean;
}

const CartAssignmentSection: React.FC<CartAssignmentSectionProps> = ({
  groups,
  vehicles,
  assignVehicleToGroup,
  getOptimizedRouteOrder,
  getVehicleDisplayName,
  isGroupAssigning,
}) => {
  // Filter only movable carts (isFixedLocation: false)
  const deliveryCarts = vehicles.filter((vehicle) => !vehicle.isFixedLocation);

  const handleVehicleChange = (groupId: string, newDeliveryCartId: string) => {
    if (newDeliveryCartId) {
      assignVehicleToGroup(groupId, newDeliveryCartId);
    }
  };

  if (groups.length === 0) {
    return (
      <div className="delivery-panel__empty-state">
        <Truck size={32} />
        <p>No unassigned groups</p>
        <p>All groups have been assigned to carts</p>
      </div>
    );
  }

  return (
    <div className="cart-assignment-container">
      {groups.map((group) => {
        const optimizedOrderIds = getOptimizedRouteOrder(group);
        const assignedDeliveryCartId = group.cart?.deliveryCartId;
        const assignedVehicle = assignedDeliveryCartId
          ? vehicles.find((v) => v.id === assignedDeliveryCartId)
          : null;

        const hasCartAssigned = !!group.cart?.deliveryCartId;
        const isAssigning = isGroupAssigning(group.id);

        return (
          <div
            key={group.id}
            className={`group-assignment-item ${
              isAssigning ? "group-assignment-item--assigning" : ""
            }`}
            style={{ borderLeftColor: group.color }}
          >
            <div className="group-assignment-item__header">
              <div className="group-assignment-item__group-info">
                <div className="group-assignment-item__text-info">
                  <div className="group-assignment-item__name">
                    {group.name}
                    {isAssigning && (
                      <Loader
                        className="group-assignment-item__loader"
                        size={14}
                      />
                    )}
                  </div>
                  <div className="group-assignment-item__count">
                    {optimizedOrderIds.length} delivery
                    {optimizedOrderIds.length !== 1 ? "s" : ""}
                    {group.deliveryRoute && (
                      <span className="group-assignment-item__distance">
                        • {group.deliveryRoute.distanceInKm.toFixed(2)} km
                      </span>
                    )}
                  </div>
                  {group.cart?.deliveryCartId && (
                    <div className="group-assignment-item__cart-info">
                      <Truck size={12} />
                      <span className="group-assignment-item__cart-name">
                        Cart: {group.cart.displayName}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="group-assignment-item__assignment-section">
                <div className="group-assignment-item__current-assignment">
                  {assignedVehicle || hasCartAssigned ? (
                    <div className="group-assignment-item__assigned-info">
                      Currently assigned to: {assignedVehicle?.name || "Delivery Cart"}
                    </div>
                  ) : (
                    <div className="group-assignment-item__not-assigned">
                      <Truck size={14} />
                      <span>Not assigned to any delivery cart</span>
                    </div>
                  )}
                </div>

                <div className="group-assignment-item__change-controls">
                  <div className="group-assignment-item__select-container">
                    <select
                      value={assignedDeliveryCartId || ""}
                      onChange={(e) =>
                        handleVehicleChange(group.id, e.target.value)
                      }
                      disabled={isAssigning}
                      className={`group-assignment-item__vehicle-select ${
                        isAssigning
                          ? "group-assignment-item__vehicle-select--disabled"
                          : ""
                      }`}
                    >
                      <option value="">Select a delivery cart...</option>
                      {deliveryCarts.map((vehicle) => (
                        <option key={vehicle.id} value={vehicle.id}>
                          {getVehicleDisplayName(vehicle)} ({vehicle.cartState})
                        </option>
                      ))}
                    </select>
                    {isAssigning && (
                      <div className="group-assignment-item__select-overlay">
                        <span>Assigning cart...</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CartAssignmentSection;